import {
  COMPONENT_TYPE_GUEST,
  COMPONENT_TYPE_ADULT_GUEST,
  COMPONENT_TYPE_CHILD_GUEST,
  FIELD_ID_FIRSTNAME,
  FIELD_ID_AGE,
  FIELD_ID_LASTNAME,
  FIELD_ID_SURNAME,
  FIELD_ID_PHONE,
  FIELD_ID_EMAIL,
  FIELD_ID_CUSTOM,
  ADULT_GUEST_BLOCK_NAME,
  CHILD_GUEST_BLOCK_NAME,
  useDragAndDrop,
  FieldOption,
  GuestInfo,
  FieldInfo,
  GUEST_TYPES,
} from '@entities/leadCollector';
import { WarningIcon, XmarkIcon } from '@shared/assets/icons';
import { UnstyledButton } from '@shared/ui/unstyled-button';
import { colors } from '@shared/tokens/colors.stylex';
import { useToast } from '@shared/hooks/useToast';
import { AddFieldButton } from './AddFieldButton';
import { useState, useEffect, useRef } from 'react';
import * as stylex from '@stylexjs/stylex';
import { FormInput } from './FormInput';
import { styles } from './styles';

interface FormFieldGroupProps {
  title: string;
  guestType?: GUEST_TYPES;
  isCanEdit: boolean;
  value: GuestInfo;
  onChange: (guest: GuestInfo, guestType: GUEST_TYPES) => void;
  number?: number;
  onDelete?: (guestType: GUEST_TYPES) => void;
  isTitleEditable?: boolean;
  onTitleChange?: (newTitle: string) => void;
}

const MAX_TITLE_LENGTH = 20;

const getAllFieldOptions = (guestInfo: GuestInfo): FieldOption[] => {
  const allOptions: FieldOption[] = [
    { dbField: FIELD_ID_LASTNAME, label: 'Фамилия', placeholder: 'Фамилия', isFIO: true },
    { dbField: FIELD_ID_FIRSTNAME, label: 'Имя', placeholder: 'Имя', isFIO: true },
    { dbField: FIELD_ID_SURNAME, label: 'Отчество', placeholder: 'Отчество', isFIO: true },
    { dbField: FIELD_ID_AGE, label: 'Возраст', placeholder: 'Возраст', isFIO: true },
    { dbField: FIELD_ID_EMAIL, label: 'E-mail', labelText: 'Ваш e-mail *' },
    {
      dbField: FIELD_ID_PHONE,
      label: 'Телефон',
      placeholder: '+ 7 (xxx) xxx-xxxx',
      labelText: 'Ваш телефон для связи *',
    },
    { dbField: FIELD_ID_CUSTOM, label: 'Свое поле', placeholder: '' },
  ];

  return allOptions.map((option) => {
    const isDisabled =
      guestInfo.FIO.some((field) => field.dbField === option.dbField) ||
      guestInfo.additionalFields?.some((field) => field.dbField === option.dbField);

    return {
      ...option,
      disabled: option.dbField === FIELD_ID_CUSTOM ? false : isDisabled,
    };
  });
};

export const FormFieldGroup = ({
  title,
  guestType = COMPONENT_TYPE_GUEST,
  isCanEdit,
  value,
  onChange,
  number,
  onDelete,
  isTitleEditable = false,
  onTitleChange,
}: FormFieldGroupProps) => {
  const isMainGuest = guestType === COMPONENT_TYPE_GUEST;
  const isChildGuest = guestType === COMPONENT_TYPE_CHILD_GUEST;
  const isAdultGuest = guestType === COMPONENT_TYPE_ADULT_GUEST;
  const isExtraGuest = isAdultGuest || isChildGuest;
  const { showToast } = useToast();
  const [guest, setGuest] = useState<GuestInfo>(value);
  const allOptions = getAllFieldOptions(guest);
  const titleRef = useRef<HTMLSpanElement>(null);
  const initialTitleRef = useRef<string>(title);

  useEffect(() => {
    if (!value) return;
    setGuest(value);
  }, [value]);

  const handleReorderFIO = (fromIndex: number, toIndex: number) => {
    setGuest((prev) => {
      const newFIO = [...prev.FIO];
      [newFIO[fromIndex], newFIO[toIndex]] = [newFIO[toIndex], newFIO[fromIndex]];
      return { ...prev, FIO: newFIO };
    });
  };

  const handleReorderAdditions = (fromIndex: number, toIndex: number) => {
    setGuest((prev) => {
      if (!prev.additionalFields) return prev;
      const newAdditionals = [...prev.additionalFields];
      [newAdditionals[fromIndex], newAdditionals[toIndex]] = [newAdditionals[toIndex], newAdditionals[fromIndex]];
      return { ...prev, additionalFields: newAdditionals };
    });
  };

  const onDeleteField = (id: string) => {
    const newGuest = structuredClone(guest);

    newGuest.additionalFields = guest.additionalFields?.filter(({ instanceId }) => instanceId !== id);
    newGuest.FIO = guest.FIO.filter(({ instanceId }) => instanceId !== id);

    if (isMainGuest) {
      const hasEmailOrPhone = newGuest.additionalFields?.some(
        ({ dbField }) => dbField === FIELD_ID_EMAIL || dbField === FIELD_ID_PHONE
      );

      if (!hasEmailOrPhone) {
        showToast(
          <div {...stylex.props(styles.warningContainer)}>
            <WarningIcon width="16" height="16" />

            <div {...stylex.props(styles.warningText)}>
              <span>У основного пользователя должно быть поле E-mail </span>
              <span>или телефон</span>
            </div>
          </div>,
          'error'
        );
        return;
      }
    }

    if (isExtraGuest && !newGuest.FIO.length && !newGuest.additionalFields?.length) {
      const blockName = isAdultGuest ? ADULT_GUEST_BLOCK_NAME : CHILD_GUEST_BLOCK_NAME;

      showToast(
        <div {...stylex.props(styles.warningContainer)}>
          <WarningIcon width="16" height="16" />

          <div {...stylex.props(styles.warningText)}>
            <span>В блоке «{blockName}» должно быть минимум</span>
            <span>одно поле</span>
          </div>
        </div>,
        'error'
      );
      return;
    }

    setGuest(newGuest);
  };

  const handleLabelChange = (id: string, newLabel: string) => {
    setGuest((prev) => {
      return {
        ...prev,
        additionalFields: prev.additionalFields?.map((field) =>
          field.instanceId === id ? { ...field, label: newLabel } : field
        ),
      };
    });
  };

  const handleFieldSelect = (option: FieldOption) => {
    const isCustomField = option.dbField === FIELD_ID_CUSTOM;

    const newField: FieldInfo = {
      instanceId: `field-${Date.now()}`,
      dbField: isCustomField ? FIELD_ID_CUSTOM : option.dbField,
      placeholder: option.placeholder || '',
      label: isCustomField ? 'Название поля' : option.labelText || '',
      value: '',
    };

    if (option.isFIO) {
      guest.FIO.push(newField);
    } else {
      guest.additionalFields?.push(newField);
    }

    setGuest({ ...guest });
  };

  const onChangeValue = (id: string, value: string) => {
    if (!isCanEdit) return;

    const field =
      guest.FIO?.find(({ instanceId }) => instanceId === id) ||
      guest.additionalFields?.find(({ instanceId }) => instanceId === id);

    if (!field) return;

    field.value = value;
    field.error = '';
    setGuest({ ...guest });
  };

  const {
    handleDragStart: handleDragStartWithLabel,
    handleDragEnd: handleDragEndWithLabel,
    handleDragOver: handleDragOverWithLabel,
    handleDrop: handleDropWithLabel,
  } = useDragAndDrop({
    onReorder: handleReorderAdditions,
  });

  const {
    handleDragStart: handleDragStartWithoutLabel,
    handleDragEnd: handleDragEndWithoutLabel,
    handleDragOver: handleDragOverWithoutLabel,
    handleDrop: handleDropWithoutLabel,
  } = useDragAndDrop({
    onReorder: handleReorderFIO,
  });

  useEffect(() => {
    onChange(guest, guestType);
  }, [guest]);

  useEffect(() => {
    if (titleRef.current && titleRef.current.textContent !== title) {
      titleRef.current.textContent = title;
    }
  }, [title]);

  const handleTitleBlur = (e: React.FocusEvent<HTMLSpanElement>) => {
    const trimmedText = e.currentTarget.textContent?.trim() || '';

    if (!trimmedText) {
      titleRef.current && (titleRef.current.textContent = initialTitleRef.current);
      onTitleChange?.(initialTitleRef.current);
      return;
    }

    const titleWithAsterisk = trimmedText.endsWith('*') ? trimmedText : `${trimmedText} *`;
    titleRef.current && (titleRef.current.textContent = titleWithAsterisk);
    onTitleChange?.(titleWithAsterisk);
  };

  const handleTitleKeyDown = (e: React.KeyboardEvent<HTMLSpanElement>) => {
    const text = e.currentTarget.textContent || '';
    if (text.length >= MAX_TITLE_LENGTH && e.key !== 'Backspace' && e.key !== 'Delete') {
      e.preventDefault();
    }
  };

  const renderTitle = () => {
    if (!isTitleEditable) {
      return <span {...stylex.props(styles.title)}>{title}</span>;
    }

    return (
      <span
        {...stylex.props(styles.editableTitle)}
        suppressContentEditableWarning
        contentEditable
        role="textbox"
        onBlur={handleTitleBlur}
        onKeyDown={handleTitleKeyDown}
        ref={titleRef}
        onMouseDownCapture={() => titleRef.current?.focus()}
      />
    );
  };

  const shouldShowNumber = isCanEdit && number !== undefined;

  return (
    <div {...stylex.props(styles.container)}>
      {shouldShowNumber && (
        <div {...stylex.props(styles.header)}>
          №{number + 1}
          {!!number && (
            <UnstyledButton onClick={() => onDelete?.(guestType)}>
              <XmarkIcon color={colors.primaryBackground} />
            </UnstyledButton>
          )}
        </div>
      )}

      {renderTitle()}

      <div {...stylex.props(styles.inputsContainer)}>
        {guest.FIO.map((field, fieldIndex) => {
          return (
            <div
              key={field.dbField}
              draggable={!isCanEdit}
              onDragStart={(e) => handleDragStartWithoutLabel(e, fieldIndex)}
              onDragEnd={handleDragEndWithoutLabel}
              onDragOver={(e) => handleDragOverWithoutLabel(e, fieldIndex)}
              onDrop={(e) => handleDropWithoutLabel(e, fieldIndex)}
            >
              <FormInput
                placeholder={field.placeholder}
                label={field.label}
                id={field.dbField}
                onDelete={() => onDeleteField(field.instanceId)}
                isCanEdit={isCanEdit}
                readOnly={field.readOnly}
                value={field.value}
                error={field.error}
                onInputChange={(value: string) => onChangeValue(field.instanceId, value)}
              />
            </div>
          );
        })}
      </div>

      {guest.additionalFields && (
        <div {...stylex.props(styles.inputsContainerWithLabel)}>
          {guest.additionalFields?.map((field, fieldIndex) => {
            return (
              <div
                key={field.dbField}
                draggable={!isCanEdit}
                onDragStart={(e) => handleDragStartWithLabel(e, fieldIndex)}
                onDragEnd={handleDragEndWithLabel}
                onDragOver={(e) => handleDragOverWithLabel(e, fieldIndex)}
                onDrop={(e) => handleDropWithLabel(e, fieldIndex)}
              >
                <FormInput
                  placeholder={field.placeholder}
                  label={field.label}
                  id={field.dbField}
                  isLabelEditable={field.dbField === FIELD_ID_CUSTOM && !isCanEdit}
                  onLabelChange={(value) => handleLabelChange(field.instanceId, value)}
                  onDelete={() => onDeleteField(field.instanceId)}
                  isCanEdit={isCanEdit}
                  readOnly={field.readOnly}
                  value={field.value}
                  error={field.error}
                  onInputChange={(value: string) => onChangeValue(field.instanceId, value)}
                />
              </div>
            );
          })}
        </div>
      )}

      {!isCanEdit && <AddFieldButton options={allOptions} onSelect={handleFieldSelect} />}
    </div>
  );
};
